import React, { useMemo } from 'react';
import useTranslate from '../../../../../customHooks/useTranslate';
import {
    ComplementaryLanguageState,
    ComplementaryServiceCategoryState,
    ComplementaryServiceTypeState,
    ZinantisServiceState,
    ZinantisRoomServiceState,
    VisibleContentScope,
} from '../../../types/zitoneoServicesTypes';
import ZitoneoServiceItem from './ZitoneoServiceItem';
import ZitoneoAvailableService from './ZitoneoAvailableService';
import { createNewHotelService } from '../../../utils/zitoneoServicesUtils';
import { useZitoneoServices } from '../../../contexts/ZitoneoContentContext';
import { useZitoneoUI } from '../../../contexts/ZitoneoUIContext';
import LoadingPlaceholder from '../../../../../muiComponents/LoadingPlaceholder';
import CategoryServiceScraper from './CategoryServiceScraper';

interface ZitoneoServicesListProps {
    title: string;
    overtitle: string;
    subtitle: string;
    serviceTypes: ComplementaryServiceTypeState[];
    selectedServices: (ZinantisServiceState | ZinantisRoomServiceState)[];
    selectedCategory: ComplementaryServiceCategoryState | null;
    onAddService: (service: ZinantisServiceState) => void;
    onVisibleServicesDetail: (idService: number) => void;
    idVisibleServicesDetail: number;
    languages: ComplementaryLanguageState[];
    roomId?: string;
    // visibleLanguage: ComplementaryLanguageState | null;
    // onVisibleLanguage: (language: ComplementaryLanguageState) => void;
}

interface ServiceGroup {
    id: string;
    name: string;
    services: (ZinantisServiceState | ZinantisRoomServiceState)[];
    isNewService: boolean;
}

interface ServiceItemsListProps {
    groups: ServiceGroup[];
    onVisibleServicesDetail: (idService: number) => void;
    idVisibleServicesDetail: number;
    roomId?: string;
}

const ServiceItemsList = ({
    groups,
    onVisibleServicesDetail,
    idVisibleServicesDetail,
    roomId,
}: ServiceItemsListProps) => (
    <ul className='zitoneo-service__list'>
        {groups.map(group => (
            <li key={group.id}>
                <ZitoneoServiceItem
                    id={group.id}
                    name={group.name}
                    isNewService={group.isNewService}
                    hotelServices={group.services}
                    onVisibleServiceDetail={onVisibleServicesDetail}
                    isVisibleServicesDetail={
                        idVisibleServicesDetail === parseInt(group.id)
                    }
                    roomId={roomId}
                />
            </li>
        ))}
    </ul>
);

const ZitoneoServicesList = ({
    title,
    overtitle,
    subtitle,
    serviceTypes,
    selectedServices,
    selectedCategory,
    onAddService,
    onVisibleServicesDetail,
    idVisibleServicesDetail,
    languages,
    roomId,
}: ZitoneoServicesListProps) => {
    const translate = useTranslate();
    const { isLoading, handleFocusHotelService, visibleContentScope } =
        useZitoneoUI();
    const { handleAddRoomService } = useZitoneoServices();

    const selectedServiceTypes = useMemo(() => {
        return serviceTypes.filter(serviceType =>
            selectedServices.some(
                hotelService =>
                    hotelService.IdServiceType === parseInt(serviceType.Id)
            )
        );
    }, [serviceTypes, selectedServices]);

    const unselectedServiceTypes = useMemo(() => {
        return serviceTypes.filter(
            serviceType =>
                !selectedServices.some(
                    hotelService =>
                        hotelService.IdServiceType === parseInt(serviceType.Id)
                )
        );
    }, [serviceTypes, selectedServices]);

    const serviceGroups = useMemo<ServiceGroup[]>(() => {
        if (visibleContentScope === VisibleContentScope.ROOM) {
            const visibleRoomServices = selectedCategory
                ? selectedServices.filter(
                      service =>
                          String(service.IdServiceCategory) ===
                          selectedCategory.Id
                  )
                : selectedServices;

            return visibleRoomServices.map(service => ({
                id: service.Id,
                name: service.ServiceType,
                services: [service],
                isNewService: false,
            }));
        }

        return selectedServiceTypes.map(service => ({
            id: service.Id,
            name: service.Name,
            isNewService: service.IsNewService,
            services: selectedServices.filter(
                selectedService =>
                    selectedService.IdServiceType === parseInt(service.Id)
            ),
        }));
    }, [
        selectedCategory,
        selectedServices,
        selectedServiceTypes,
        visibleContentScope,
    ]);

    if (isLoading) {
        return (
            <div className='zitoneo-service__page'>
                <LoadingPlaceholder
                    variant='rounded'
                    width={'100%'}
                    height={'100%'}
                />
            </div>
        );
    }

    return (
        <div className='zitoneo-service__page'>
            <div className='services-page__header__container'>
                <div className='services-page__header'>
                    <div className='services-page__titles_container'>
                        <h3 className='services-page__overtitle'>
                            {`${translate('category')} · ${overtitle}`}
                        </h3>
                        <div className='services-page__title-row'>
                            <h2 className='services-page__title'>
                                {translate(title)}
                            </h2>
                            {visibleContentScope ===
                                VisibleContentScope.HOTEL &&
                            selectedCategory ? (
                                <CategoryServiceScraper
                                    categoryId={selectedCategory.Id}
                                />
                            ) : null}
                        </div>

                        <h4 className='services-page__subtitle'>
                            {translate(subtitle)}
                        </h4>
                    </div>
                    {visibleContentScope === VisibleContentScope.HOTEL && (
                        <div className='services-page__stats__container'>
                            <div className='service-stats__item'>
                                <div className='service-stats__value'>
                                    {selectedServiceTypes.length}
                                </div>
                                <div className='service-stats__title'>
                                    {translate('selected')}
                                </div>
                            </div>
                            <div className='service-stats__item'>
                                <div className='service-stats__value'>
                                    {unselectedServiceTypes.length}
                                </div>
                                <div className='service-stats__title'>
                                    {translate('available_plural')}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className='services-page__language-selector__container'>
                    <p>{translate('editing_in')}:</p>
                    <ul className='services-page__language-selector'>
                        {languages.map(language => (
                            <li key={language.Id}>
                                <button
                                    // className={`language-button
                                    //     ${
                                    //     visibleLanguage?.Id === language.Id
                                    //         ? '--active'
                                    //         : ''
                                    // }`}
                                    className='language-button cursor-default'
                                    // onClick={() => onVisibleLanguage(language)}
                                >
                                    {language.Code.toUpperCase()}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            <div className='service-page__section'>
                <div className='service-section__header'>
                    <h5 className='service-section__header__title'>
                        {translate('selected_services')}
                    </h5>
                    <p className='service-section__header__description'>
                        {`${selectedServiceTypes.length} ${translate(
                            'active'
                        )} · ${translate('click_to_edit_attributes')}`}
                    </p>
                </div>
                {selectedServiceTypes.length ? (
                    <ServiceItemsList
                        groups={serviceGroups}
                        onVisibleServicesDetail={onVisibleServicesDetail}
                        idVisibleServicesDetail={idVisibleServicesDetail}
                        roomId={roomId}
                    />
                ) : (
                    <div className='zitoneo-service__item'>
                        <div className='zitoneo-service__header'>
                            <label className='zitoneo-service__label'>
                                {translate('no_services_selected')}
                            </label>
                        </div>
                    </div>
                )}
            </div>

            <div className='service-page__section'>
                <div className='service-section__header'>
                    <h5 className='service-section__header__title'>
                        {translate('available_to_add')}
                    </h5>
                    <p className='service-section__header__description'>
                        {`${unselectedServiceTypes.length} ${translate(
                            'services_left_in_this_category'
                        )}`}
                    </p>
                </div>
                <ul className='zitoneo-service__available-list'>
                    {unselectedServiceTypes.length ? (
                        unselectedServiceTypes.map(service => (
                            <li key={service.Id} className='flex'>
                                <ZitoneoAvailableService
                                    service={service}
                                    onClick={async () => {
                                        if (
                                            visibleContentScope ===
                                                VisibleContentScope.ROOM &&
                                            roomId
                                        ) {
                                            const roomServiceId =
                                                await handleAddRoomService(
                                                    roomId,
                                                    service
                                                );
                                            onVisibleServicesDetail(
                                                parseInt(roomServiceId)
                                            );
                                        } else if (selectedCategory) {
                                            const newService =
                                                createNewHotelService(
                                                    service,
                                                    selectedCategory
                                                );

                                            handleFocusHotelService(
                                                newService.Id
                                            );
                                            onAddService(newService);

                                            if (
                                                idVisibleServicesDetail !==
                                                parseInt(service.Id)
                                            ) {
                                                onVisibleServicesDetail(
                                                    parseInt(service.Id)
                                                );
                                            }
                                        }
                                    }}
                                />
                            </li>
                        ))
                    ) : (
                        <li className='--no-service'>
                            <div className='zitoneo-service__available-item'>
                                <p>{translate('no_services_available')}</p>
                            </div>
                        </li>
                    )}
                </ul>
            </div>
        </div>
    );
};

export default ZitoneoServicesList;
